import dayjs from 'dayjs';
import { commissionRows, netOfCard, traceUsage, tuaRows } from './ktCards';
import {
  billTax, dmy, inputWarnings, invoiceLines, matchPayoo, rateOn, splitVat,
  type BookScope, type KtState, type MappingRule, type PayMethod, type Voucher,
} from './ktLogic';

/**
 * Sổ kế toán từ cùng một nguồn chứng từ (SRS 1.1): mỗi chứng từ có "Phạm vi sổ", bút toán sinh theo bảng Mapping.
 * Ranh giới: Sổ Thuế là sổ kế toán tài chính, ghi đủ doanh thu và dòng tiền; Sổ Quản trị chỉ khác ở bút toán
 * nội bộ không phát sinh tiền, và mọi khác biệt đều hiện trên báo cáo giải trình.
 */

export const SCOPE: Record<BookScope, string> = { both: 'Cả 2 sổ', tax: 'Chỉ Sổ Thuế', mgmt: 'Chỉ Sổ Quản trị' };
export type Book = 'tax' | 'mgmt';
export const BOOK: Record<Book, string> = { tax: 'Sổ Thuế', mgmt: 'Sổ Quản trị' };

export const ACCOUNT: Record<string, string> = {
  1111: 'Tiền mặt', 1121: 'Tiền gửi ngân hàng', 113: 'Tiền đang chuyển', 131: 'Phải thu khách hàng', 1331: 'Thuế GTGT được khấu trừ',
  152: 'Vật tư', 1561: 'Hàng hóa', 214: 'Hao mòn TSCĐ', 242: 'Chi phí trả trước', 331: 'Phải trả người bán', 33311: 'Thuế GTGT đầu ra',
  335: 'Chi phí phải trả', 711: 'Thu nhập khác', 3387: 'Doanh thu chưa thực hiện', 3388: 'Phải trả khác (ví trả trước)', 5111: 'Doanh thu bán hàng', 5113: 'Doanh thu dịch vụ',
  5213: 'Giảm trừ doanh thu', 632: 'Giá vốn', 635: 'Chi phí tài chính', 6411: 'Hoa hồng bán hàng', 6417: 'Phí cà thẻ', 6423: 'Phân bổ chi phí', 6424: 'Khấu hao TSCĐ', 6428: 'Chi phí khác',
};
export const accountName = (a: string) => ACCOUNT[a] || '';

/** Tài khoản tiền, công nợ, thuế, doanh thu: bút toán chạm các tài khoản này bắt buộc ghi cả 2 sổ */
const GUARDED = /^(111|112|113|131|133|331|333|3387|3388|511|521|711)/;
export const isGuarded = (account: string) => GUARDED.test(account);

/** Chặn đưa tiền hoặc doanh thu ra khỏi Sổ Thuế */
export function scopeError(debit: string, credit: string, scope: BookScope): string | null {
  if (scope === 'both' || (!isGuarded(debit) && !isGuarded(credit))) return null;
  return 'Bút toán có tiền, công nợ, thuế hoặc doanh thu phải ghi cả 2 sổ. Chỉ bút toán nội bộ không phát sinh tiền mới được ghi riêng một sổ.';
}

export const DEFAULT_RULES: MappingRule[] = [
  { id: 'sale_service', label: 'Doanh thu dịch vụ đã thực hiện', debit: '131', credit: '5113', scope: 'both' },
  { id: 'sale_product', label: 'Doanh thu sản phẩm', debit: '131', credit: '5111', scope: 'both' },
  { id: 'sale_package', label: 'Bán thẻ liệu trình (chưa thực hiện)', debit: '131', credit: '3387', scope: 'both' },
  { id: 'vat_out', label: 'Thuế GTGT đầu ra', debit: '131', credit: '33311', scope: 'both' },
  { id: 'receipt_cash', label: 'Thu tiền mặt', debit: '1111', credit: '131', scope: 'both' },
  { id: 'receipt_bank', label: 'Thu chuyển khoản, VietQR', debit: '1121', credit: '131', scope: 'both' },
  { id: 'receipt_card', label: 'Thu qua POS Payoo, trả góp', debit: '113', credit: '131', scope: 'both' },
  { id: 'receipt_prepaid', label: 'Thu bằng ví trả trước', debit: '3388', credit: '131', scope: 'both' },
  { id: 'settle_net', label: 'Payoo trả về ngân hàng', debit: '1121', credit: '113', scope: 'both' },
  { id: 'settle_fee', label: 'Phí cà thẻ POS', debit: '6417', credit: '113', scope: 'both' },
  { id: 'settle_fee_inst', label: 'Phí trả góp Payoo', debit: '635', credit: '113', scope: 'both' },
  { id: 'refund_net', label: 'Hoàn tiền: giảm doanh thu', debit: '5213', credit: '1111', scope: 'both' },
  { id: 'refund_vat', label: 'Hoàn tiền: giảm thuế đầu ra', debit: '33311', credit: '1111', scope: 'both' },
  { id: 'refund_net_bank', label: 'Hoàn tiền qua ngân hàng: giảm doanh thu', debit: '5213', credit: '1121', scope: 'both' },
  { id: 'purchase_vat', label: 'Thuế GTGT mua vào', debit: '1331', credit: '331', scope: 'both' },
  { id: 'card_use', label: 'Kết chuyển buổi liệu trình đã làm', debit: '3387', credit: '5113', scope: 'both' },
  { id: 'refund_card', label: 'Hoàn thẻ bằng tiền mặt', debit: '3387', credit: '1111', scope: 'both' },
  { id: 'refund_card_bank', label: 'Hoàn thẻ qua ngân hàng', debit: '3387', credit: '1121', scope: 'both' },
  { id: 'refund_vat_bank', label: 'Hoàn tiền qua ngân hàng: giảm thuế đầu ra', debit: '33311', credit: '1121', scope: 'both' },
  { id: 'card_penalty', label: 'Giữ lại phạt hủy thẻ, phí cà thẻ', debit: '3387', credit: '711', scope: 'both' },
  { id: 'card_test_fee', label: 'Giữ lại phí xét nghiệm', debit: '3387', credit: '5113', scope: 'both' },
  { id: 'commission', label: 'Trích trước hoa hồng Sale, CSKH', debit: '6411', credit: '335', scope: 'both' },
  { id: 'commission_back', label: 'Truy thu hoa hồng do hoàn tiền', debit: '335', credit: '6411', scope: 'both' },
  { id: 'tua', label: 'Trích trước tua ekip', debit: '632', credit: '335', scope: 'both' },
  { id: 'expense', label: 'Chi phí chi bằng tiền mặt', debit: '6428', credit: '1111', scope: 'both', forVoucher: true },
  { id: 'dep_straight', label: 'Khấu hao đường thẳng', debit: '6424', credit: '214', scope: 'tax', forVoucher: true, note: 'Theo khung thuế' },
  { id: 'dep_shot', label: 'Khấu hao máy theo số shot', debit: '632', credit: '214', scope: 'mgmt', forVoucher: true, note: 'Theo số shot thực tế' },
  { id: 'alloc_tax', label: 'Phân bổ TK 242 theo kỳ thuế', debit: '6423', credit: '242', scope: 'tax', forVoucher: true },
  { id: 'alloc_mgmt', label: 'Phân bổ TK 242 theo kỳ quản trị', debit: '6423', credit: '242', scope: 'mgmt', forVoucher: true },
];

export interface Entry {
  id: string; date: string; docNo: string; docKind: string; desc: string;
  ruleId: string; debit: string; credit: string; amount: number; scope: BookScope; reason?: string;
}

type EntryState = Pick<KtState, 'bills' | 'rules' | 'rates' | 'services' | 'vouchers' | 'inputInvoices' | 'payoo' | 'cards' | 'usageLog' | 'refundCases' | 'commissionRates' | 'locks'>;

const RECEIPT: Record<PayMethod, string> = {
  cash: 'receipt_cash', transfer: 'receipt_bank', vietqr: 'receipt_bank', payoo: 'receipt_card', installment: 'receipt_card', prepaid: 'receipt_prepaid',
};

/** Sinh toàn bộ bút toán từ chứng từ; dữ liệu nguồn không đổi thì kết quả không đổi */
export function buildEntries(s: EntryState): Entry[] {
  // Dữ liệu lưu trên máy từ bản trước có thể chưa có dòng Mapping mới: dùng dòng mặc định
  const rule = (id: string) => s.rules.find(r => r.id === id) ?? DEFAULT_RULES.find(r => r.id === id)!;
  const out: Entry[] = [];
  const post = (ruleId: string, amount: number, date: string, docNo: string, docKind: string, desc: string) => {
    if (!amount) return;
    const r = rule(ruleId);
    out.push({ id: `${docNo}-${ruleId}-${out.length}`, date, docNo, docKind, desc, ruleId, debit: r.debit, credit: r.credit, amount, scope: r.scope });
  };

  s.bills.filter(b => b.status === 'valid').forEach(b => {
    if (b.kind !== 'deposit') {
      invoiceLines(s, b).forEach(l => {
        const sv = s.services.find(x => x.id === l.serviceId);
        const v = splitVat(l.gross, rateOn(s.rates, l.serviceId, b.date)?.rate ?? null);
        const ruleId = sv?.type === 'product' ? 'sale_product' : sv?.type === 'package' || b.kind === 'card' ? 'sale_package' : 'sale_service';
        post(ruleId, v.net, b.date, b.no, 'Bill', `${l.name} · ${b.customer}`);
        post('vat_out', v.vat, b.date, b.no, 'Bill', `VAT ${l.name}`);
      });
    }
    b.payments.forEach(p => post(RECEIPT[p.method], p.amount, p.date || b.date, b.no, 'Thu tiền', `${b.customer}${p.ref ? ` · ${p.ref}` : ''}`));
    // Hoàn thẻ đã có hồ sơ duyệt thì bút toán đi theo hồ sơ
    if (s.refundCases.some(r => r.billId === b.id && r.status === 'paid')) return;
    b.refunds.forEach(x => {
      const t = billTax(s, b);
      const gross = t.net + t.vat;
      const vat = gross ? Math.round((x.amount * t.vat) / gross) : 0;
      // Hoàn bằng tiền mặt ghi giảm quỹ (1111); hoàn chuyển khoản / Payoo ghi giảm ngân hàng (1121), như hồ sơ hoàn thẻ
      const cash = x.method === 'cash';
      post(b.kind === 'card' ? (cash ? 'refund_card' : 'refund_card_bank') : (cash ? 'refund_net' : 'refund_net_bank'), x.amount - vat, x.date, b.no, 'Hoàn tiền', `${b.customer} · ${x.reason || ''}`);
      post(cash ? 'refund_vat' : 'refund_vat_bank', vat, x.date, b.no, 'Hoàn tiền', `VAT hoàn ${b.customer}`);
    });
  });

  // Dòng trùng (cùng giao dịch xuất hai lần) không ghi sổ lần hai
  matchPayoo(s.payoo, s.bills).matches.filter(m => m.bill && m.status !== 'dup').forEach(m => {
    const inst = m.bill!.payments.find(p => p.ref.toUpperCase() === m.row.ref.toUpperCase())?.method === 'installment';
    post('settle_net', m.row.net, m.row.date, m.row.ref, 'Đối soát', `Payoo ${m.bill!.no}`);
    post(inst ? 'settle_fee_inst' : 'settle_fee', m.row.fee, m.row.date, m.row.ref, 'Đối soát', `Phí ${m.bill!.customer}`);
  });

  s.inputInvoices.forEach(i => {
    out.push({ id: `${i.no}-cost`, date: i.date, docNo: i.no, docKind: 'Hóa đơn mua vào', desc: `${i.desc} · ${i.seller}`, ruleId: 'purchase', debit: i.debit, credit: '331', amount: i.net, scope: 'both' });
    post('purchase_vat', i.vat, i.date, i.no, 'Hóa đơn mua vào', `VAT ${i.seller}`);
  });

  // Kết chuyển 3387 sang 511 theo giá trị thực tế từng buổi: buổi tặng giá trị 0 nên không sinh bút toán
  s.usageLog.filter(e => !e.cancelled).forEach(e => {
    const t = traceUsage(s, e);
    if (!t) return;
    post('card_use', t.net, e.date, e.no, 'Trừ thẻ', `${t.lines.map(l => l.name).join(', ')} · ${t.card.customerName} · ${t.origin}`);
  });

  s.refundCases.filter(r => r.status === 'paid' && r.paidDate).forEach(r => {
    const card = s.cards.find(c => c.id === r.cardId);
    const net = card ? netOfCard(s, card, r.refundable) : r.refundable;
    const part = (x: number) => (r.refundable ? Math.round((x * net) / r.refundable) : 0);
    const who = card?.customerName || '';
    const [netRule, vatRule] = r.method === 'cash' ? ['refund_card', 'refund_vat'] : ['refund_card_bank', 'refund_vat_bank'];
    post(netRule, part(r.net), r.paidDate!, r.no, 'Hoàn thẻ', `Hoàn tiền nét · ${who}`);
    post(vatRule, r.net - part(r.net), r.paidDate!, r.no, 'Hoàn thẻ', `VAT hoàn · ${who}`);
    post('card_penalty', part(r.penalty + r.posFee), r.paidDate!, r.no, 'Hoàn thẻ', `Phạt hủy, phí cà thẻ · ${who}`);
    post('card_test_fee', part(r.testFee), r.paidDate!, r.no, 'Hoàn thẻ', `Phí xét nghiệm · ${who}`);
  });

  // Hoa hồng và tua trích trước theo tháng, chỉ từ doanh thu đã khóa sổ
  const monthEnd = (ym: string) => dayjs(`${ym}-01`).endOf('month').format('YYYY-MM-DD');
  const byMonth = (rows: { ym: string; amount: number }[]) => rows.reduce<Record<string, number>>((m, r) => { m[r.ym] = (m[r.ym] || 0) + r.amount; return m; }, {});
  const commissions = commissionRows(s);
  Object.entries(byMonth(commissions.filter(r => !r.clawback))).forEach(([ym, amount]) => post('commission', amount, monthEnd(ym), `HH-${ym}`, 'Bảng hoa hồng', `Hoa hồng Sale, CSKH tháng ${ym}`));
  Object.entries(byMonth(commissions.filter(r => r.clawback))).forEach(([ym, amount]) => post('commission_back', -amount, monthEnd(ym), `HH-${ym}`, 'Bảng hoa hồng', `Truy thu hoa hồng tháng ${ym}`));
  Object.entries(byMonth(tuaRows(s))).forEach(([ym, amount]) => post('tua', amount, monthEnd(ym), `TUA-${ym}`, 'Bảng tua', `Tua ekip tháng ${ym}`));

  s.vouchers.forEach(v => {
    const r = rule(v.ruleId);
    out.push({ id: v.id, date: v.date, docNo: v.no, docKind: 'Chứng từ', desc: v.desc, ruleId: v.ruleId, debit: r.debit, credit: r.credit, amount: v.amount, scope: v.scope, reason: v.reason });
  });
  return out.sort((a, b) => a.date.localeCompare(b.date) || a.docNo.localeCompare(b.docNo));
}

export const inBook = (e: Entry, book: Book) => e.scope === 'both' || e.scope === book;
export const inPeriod = (e: Entry, ym: string) => e.date.startsWith(ym);

const isRevenue = (a: string) => a.startsWith('511') || a.startsWith('515') || a.startsWith('711') || a.startsWith('521');
const isExpense = (a: string) => /^(632|635|641|642|811)/.test(a);
/** Ảnh hưởng của một bút toán tới lợi nhuận */
export const profitEffect = (e: Entry) => (isRevenue(e.credit) || isExpense(e.credit) ? e.amount : 0) - (isRevenue(e.debit) || isExpense(e.debit) ? e.amount : 0);

export interface PlRow { key: string; label: string; tax: number; mgmt: number }
const PL_LINES: { key: string; label: string; match: (a: string) => boolean; sign: number }[] = [
  { key: 'rev', label: 'Doanh thu', match: a => a.startsWith('511'), sign: 1 },
  { key: 'less', label: 'Giảm trừ doanh thu', match: a => a.startsWith('521'), sign: 1 },
  { key: 'other', label: 'Thu nhập khác', match: a => a.startsWith('711'), sign: 1 },
  { key: 'cogs', label: 'Giá vốn', match: a => a.startsWith('632'), sign: 1 },
  { key: 'sell', label: 'Chi phí bán hàng', match: a => a.startsWith('641'), sign: 1 },
  { key: 'admin', label: 'Chi phí quản lý', match: a => a.startsWith('642'), sign: 1 },
  { key: 'fin', label: 'Chi phí tài chính', match: a => a.startsWith('635'), sign: 1 },
];
const lineValue = (entries: Entry[], match: (a: string) => boolean) =>
  entries.reduce((t, e) => t + (match(e.credit) ? e.amount : 0) - (match(e.debit) ? e.amount : 0), 0);

/** Báo cáo P&L hai sổ cùng kỳ và phần chênh lệch */
export function plReport(entries: Entry[], ym: string) {
  const of = (book: Book) => entries.filter(e => inBook(e, book) && inPeriod(e, ym));
  const tax = of('tax'); const mgmt = of('mgmt');
  const rows: PlRow[] = PL_LINES.map(l => ({ key: l.key, label: l.label, tax: lineValue(tax, l.match), mgmt: lineValue(mgmt, l.match) }));
  const profit = { tax: tax.reduce((t, e) => t + profitEffect(e), 0), mgmt: mgmt.reduce((t, e) => t + profitEffect(e), 0) };
  return { rows, profit };
}

/** Giải trình chênh lệch: chứng từ chỉ có ở một sổ, chênh lệch lợi nhuận phải bằng đúng tổng các dòng này */
export function diffReport(entries: Entry[], ym: string) {
  const rows = entries.filter(e => e.scope !== 'both' && inPeriod(e, ym)).map(e => ({
    ...e, effect: (e.scope === 'mgmt' ? 1 : -1) * profitEffect(e),
  }));
  return { rows, total: rows.reduce((t, r) => t + r.effect, 0) };
}

export interface TrialRow { account: string; name: string; debit: number; credit: number }
export function trialBalance(entries: Entry[], book: Book, ym: string): TrialRow[] {
  const map = new Map<string, TrialRow>();
  const row = (a: string) => { if (!map.has(a)) map.set(a, { account: a, name: accountName(a), debit: 0, credit: 0 }); return map.get(a)!; };
  entries.filter(e => inBook(e, book) && inPeriod(e, ym)).forEach(e => { row(e.debit).debit += e.amount; row(e.credit).credit += e.amount; });
  return Array.from(map.values()).sort((a, b) => a.account.localeCompare(b.account));
}

export function validateVoucher(s: Pick<KtState, 'rules'>, v: Pick<Voucher, 'ruleId' | 'amount' | 'scope' | 'reason' | 'desc' | 'date'>): string[] {
  const errors: string[] = [];
  const r = s.rules.find(x => x.id === v.ruleId);
  if (!r) return ['Chọn loại chứng từ'];
  if (!v.date) errors.push('Chọn ngày chứng từ');
  if (!v.desc.trim()) errors.push('Nhập diễn giải');
  if (!(v.amount > 0)) errors.push('Số tiền phải lớn hơn 0');
  const guard = scopeError(r.debit, r.credit, v.scope);
  if (guard) errors.push(guard);
  if (v.scope !== 'both' && v.reason.trim().length < 5) errors.push('Chứng từ ghi riêng một sổ phải có lý do để giải trình');
  return errors;
}

// ===================== Tờ khai 01/GTGT =====================

export const RATE_ROWS = [0, 5, 8, 10];

/** Số liệu tờ khai theo kỳ tháng: đầu ra lấy từ hóa đơn đã xuất, đầu vào từ hóa đơn mua vào đủ điều kiện */
export function vatReturn(s: Pick<KtState, 'bills' | 'rates' | 'services' | 'inputInvoices'>, ym: string) {
  const inMonth = (t?: number) => !!t && dayjs(t).format('YYYY-MM') === ym;
  const issued = s.bills.filter(b => b.invoice.no && inMonth(b.invoice.at) && b.status === 'valid');
  const buckets: Record<string, { net: number; vat: number }> = { exempt: { net: 0, vat: 0 } };
  RATE_ROWS.forEach(r => { buckets[r] = { net: 0, vat: 0 }; });
  issued.forEach(b => invoiceLines(s, b).forEach(l => {
    const rate = rateOn(s.rates, l.serviceId, b.date)?.rate ?? null;
    const v = splitVat(l.gross, rate);
    const key = rate === null ? 'exempt' : String(rate);
    if (!buckets[key]) buckets[key] = { net: 0, vat: 0 };
    buckets[key].net += v.net; buckets[key].vat += v.vat;
  }));
  const inputs = s.inputInvoices.filter(i => i.date.startsWith(ym));
  const bad = inputs.filter(i => inputWarnings(s.inputInvoices, i).some(w => w.includes('chưa đủ điều kiện')));
  const input = { count: inputs.length, net: inputs.reduce((t, i) => t + i.net, 0), vat: inputs.reduce((t, i) => t + i.vat, 0) };
  const deductible = input.vat - bad.reduce((t, i) => t + i.vat, 0);
  const taxedNet = RATE_ROWS.reduce((t, r) => t + buckets[r].net, 0);
  const outputVat = RATE_ROWS.reduce((t, r) => t + buckets[r].vat, 0);
  const totalNet = taxedNet + buckets.exempt.net;
  const pending = s.bills.filter(b => b.status === 'valid' && b.kind !== 'deposit' && b.invoice.status === 'none' && b.date.startsWith(ym));
  const needsAdjust = s.bills.filter(b => b.invoice.status === 'needs_adjust' && b.date.startsWith(ym));
  const net36 = outputVat - deductible;
  return {
    ym, issued: issued.length, buckets, input, deductible, notDeductible: bad, totalNet, outputVat, net36,
    payable: Math.max(0, net36), carry: Math.max(0, -net36), pending, needsAdjust,
  };
}

const xmlEsc = (t: string | number) => String(t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Bản nháp để đối chiếu số liệu: chưa theo cấu trúc XSD của HTKK / eTax, không nộp trực tiếp được */
export function vatReturnXml(r: ReturnType<typeof vatReturn>) {
  const ct = (code: string, v: number) => `    <ct${code}>${xmlEsc(v)}</ct${code}>`;
  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<ToKhai01GTGT trangThai="BAN_NHAP_CHUA_THEO_XSD_HTKK">',
    `  <KyKeKhai>${xmlEsc(dmy(`${r.ym}-01`).slice(3))}</KyKeKhai>`,
    '  <ChiTieu>',
    ct('23', r.input.net), ct('24', r.input.vat), ct('25', r.deductible), ct('26', r.buckets.exempt.net),
    ct('29', r.buckets[0].net), ct('30', r.buckets[5].net), ct('31', r.buckets[5].vat),
    ct('32', r.buckets[10].net + r.buckets[8].net), ct('33', r.buckets[10].vat + r.buckets[8].vat),
    ct('34', r.totalNet), ct('35', r.outputVat), ct('36', r.net36), ct('40', r.payable), ct('43', r.carry),
    '  </ChiTieu>',
    `  <PhuLucGiamThue><DoanhThu8>${xmlEsc(r.buckets[8].net)}</DoanhThu8><ThueGTGT8>${xmlEsc(r.buckets[8].vat)}</ThueGTGT8></PhuLucGiamThue>`,
    '</ToKhai01GTGT>',
  ].join('\n');
}

// ===================== Bảng giá =====================

/** Bảng giá hiển thị cả giá có và chưa VAT tại hai ngày; cả 2 cách xử lý khi đổi thuế suất để Kế toán trưởng chọn */
export function priceRows(s: Pick<KtState, 'services' | 'rates'>, today: string, cutover: string) {
  return s.services.map(sv => {
    const now = rateOn(s.rates, sv.id, today)?.rate ?? null;
    const next = rateOn(s.rates, sv.id, cutover)?.rate ?? null;
    const cur = splitVat(sv.price, now);
    return {
      sv, now, next, net: cur.net, vat: cur.vat,
      /** Giữ nguyên giá khách trả: giá chưa VAT giảm */
      keepGrossNet: splitVat(sv.price, next).net,
      /** Giữ nguyên giá chưa VAT: giá khách trả tăng */
      keepNetGross: cur.net + (next ? Math.round((cur.net * next) / 100) : 0),
    };
  });
}
